import React, { ReactNode, useEffect, useReducer, useState } from 'react';
import { ViewerItem } from 'presentations/api/schema/user';
import DashboardDocument from 'presentations/views/components/DashboardDocument';
import { useSinglePageApp } from 'presentations/views/hooks';
import DashboardLoadingPage from 'presentations/views/components/pages/DashboardLoadingPage';
import { MeetingWidgetProvider } from 'presentations/reducers/MeetingWidget';
import { AssistantWidgetProvider } from 'presentations/reducers/AssistantWidget';
import { useChat } from 'react-live-chat-loader';
import AppInstallBanner from 'presentations/views/components/atoms/AppInstallBanner';
import DashboardSnackbar from 'presentations/views/components/organisms/DashboardSnackbar';
import DashboardUserInvitForm from 'presentations/views/components/organisms/DashboardUserInviteForm';
import { SeverityType } from 'presentations/views/components/atoms/Snackbar';
import { ProjectListItem } from 'presentations/api/schema/project';
import { useRouter } from 'next/router';
import DashboardSeatEditForm from 'presentations/views/components/organisms/DashboardSeatEditForm';
import ErrorPage from 'presentations/views/components/pages/ErrorPage';
import { apiPath } from 'presentations/routes/path';
import { get } from 'infras/rest_client';

declare global {
  interface Window {
    webkit: any;
  }
}

export interface State {
  viewer: ViewerItem | null;
  title: string | null;
  isOpenUserInviteForm: boolean;
  isOpenSeatEditForm: boolean;
  snackbarMessage: string | ReactNode | null | undefined;
  snackbarSeverity: SeverityType;
  projects: ProjectListItem[];
  hasCalendarIntegrationError: boolean;
}

const initialState: State = {
  viewer: null,
  title: null,
  isOpenUserInviteForm: false,
  isOpenSeatEditForm: false,
  snackbarMessage: undefined,
  snackbarSeverity: SeverityType.Error,
  hasCalendarIntegrationError: false,
  projects: [],
};

export const setViewer = (viewer: ViewerItem | null) =>
  ({
    type: 'SET_VIEWER',
    viewer,
  } as const);
export const setTitle = (title: string | null) =>
  ({
    type: 'SET_TITLE',
    title,
  } as const);
export const setIsOpenUserInviteForm = (isOpenUserInviteForm: boolean) =>
  ({
    type: 'SET_IS_OPEN_USER_INVITE_FORM',
    isOpenUserInviteForm,
  } as const);
export const setIsOpenSeatEditForm = (isOpenSeatEditForm: boolean) =>
  ({
    type: 'SET_IS_OPEN_SEAT_EDIT_FORM',
    isOpenSeatEditForm,
  } as const);
export const setSuccessMessage = (
  message: string | ReactNode | null | undefined,
) =>
  ({
    type: 'SET_SUCCESS_MESSAGE',
    message,
  } as const);
export const setInfoMessage = (
  message: string | ReactNode | null | undefined,
) =>
  ({
    type: 'SET_INFO_MESSAGE',
    message,
  } as const);
export const setWarningMessage = (
  message: string | ReactNode | null | undefined,
) =>
  ({
    type: 'SET_WARNING_MESSAGE',
    message,
  } as const);
export const setErrorMessage = (
  message: string | ReactNode | null | undefined,
) =>
  ({
    type: 'SET_ERROR_MESSAGE',
    message,
  } as const);
export const setHasCalendarIntegrationError = (
  hasCalendarIntegrationError: boolean,
) =>
  ({
    type: 'SET_HAS_CALENDAR_INTEGRATION_ERROR',
    hasCalendarIntegrationError,
  } as const);

export const setProjects = (projects: ProjectListItem[]) =>
  ({
    type: 'SET_PROJECTS',
    projects,
  } as const);

export type Action =
  | ReturnType<typeof setViewer>
  | ReturnType<typeof setTitle>
  | ReturnType<typeof setIsOpenUserInviteForm>
  | ReturnType<typeof setIsOpenSeatEditForm>
  | ReturnType<typeof setSuccessMessage>
  | ReturnType<typeof setInfoMessage>
  | ReturnType<typeof setWarningMessage>
  | ReturnType<typeof setErrorMessage>
  | ReturnType<typeof setHasCalendarIntegrationError>
  | ReturnType<typeof setProjects>;

const reducer = (state: State = initialState, action: Action) => {
  // console.debug(action.type, action);
  switch (action.type) {
    case 'SET_VIEWER':
      if (
        action.viewer &&
        typeof window.webkit?.messageHandlers?.setViewer?.postMessage ===
          'function'
      ) {
        window.webkit.messageHandlers.setViewer.postMessage(action.viewer);
      }
      return { ...state, viewer: action.viewer };
    case 'SET_TITLE':
      return { ...state, title: action.title };
    case 'SET_IS_OPEN_USER_INVITE_FORM':
      return {
        ...state,
        isOpenUserInviteForm: action.isOpenUserInviteForm,
      };
    case 'SET_IS_OPEN_SEAT_EDIT_FORM':
      return {
        ...state,
        isOpenSeatEditForm: action.isOpenSeatEditForm,
      };
    case 'SET_SUCCESS_MESSAGE':
      return {
        ...state,
        snackbarMessage: action.message,
        snackbarSeverity: SeverityType.Success,
      };
    case 'SET_INFO_MESSAGE':
      return {
        ...state,
        snackbarMessage: action.message,
        snackbarSeverity: SeverityType.Info,
      };
    case 'SET_WARNING_MESSAGE':
      return {
        ...state,
        snackbarMessage: action.message,
        snackbarSeverity: SeverityType.Warning,
      };
    case 'SET_ERROR_MESSAGE':
      return {
        ...state,
        snackbarMessage: action.message,
        snackbarSeverity: SeverityType.Error,
      };
    case 'SET_PROJECTS':
      return {
        ...state,
        projects: action.projects,
        initialized: true,
      };
    case 'SET_HAS_CALENDAR_INTEGRATION_ERROR':
      return {
        ...state,
        hasCalendarIntegrationError: action.hasCalendarIntegrationError,
      };
    default:
      return state;
  }
};

const DashboardPortalContext = React.createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: initialState,
  dispatch: () => {},
});

const DashboardPortalProvider = ({ children }: { children: ReactNode }) => {
  const app = useSinglePageApp();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [chatState, loadChat] = useChat();
  const router = useRouter();

  useEffect(() => {
    if (!app.state.viewer) return;
    if (app.state.viewer.locale !== router.locale) {
      router.replace(router.asPath, undefined, {
        locale: app.state.viewer.locale,
      });
    }
    dispatch(setViewer(app.state.viewer));
    loadChat({ open: false });
    window.Intercom &&
      window.Intercom('update', { hide_default_launcher: true });
  }, [app.state.viewer]);

  const fetchAccountCalendars = async () => {
    const result = await get(apiPath('/v1/calendars?isReadable=1'));
    if (result.isSuccess) {
      dispatch(
        setHasCalendarIntegrationError(
          typeof result.errors === 'object' &&
            Object.keys(result.errors).length > 0 &&
            Object.values(result.errors)
              .flatMap((e) => e)
              .some(
                (e: any) =>
                  typeof e === 'object' &&
                  (e.code === 'WARN-204002' || e.code === 'WARN-204004'),
              ),
        ),
      );
    }
  };

  useEffect(() => {
    // カレンダー連携エラーがあるかどうかを反映
    fetchAccountCalendars();
  }, []);

  if (app.state.errorStatusCode) {
    return <ErrorPage statusCode={app.state.errorStatusCode} />;
  }

  return (
    <DashboardPortalContext.Provider value={{ state, dispatch }}>
      <DashboardDocument title={state.title}>
        {state.viewer ? (
          <>
            <AssistantWidgetProvider>
              <MeetingWidgetProvider>{children}</MeetingWidgetProvider>
            </AssistantWidgetProvider>
            <AppInstallBanner />
            <DashboardSnackbar />
            <DashboardUserInvitForm viewer={state.viewer} />
            <DashboardSeatEditForm viewer={state.viewer} />
          </>
        ) : (
          <DashboardLoadingPage />
        )}
      </DashboardDocument>
    </DashboardPortalContext.Provider>
  );
};

export { DashboardPortalContext, DashboardPortalProvider };
